import React, { createContext, useContext, useMemo, ReactNode } from 'react';
import { AnalyticsService } from './AnalyticsService';
import { Ga4AnalyticsClient } from './Ga4AnalyticsClient';
import { getMeasurementId, isAnalyticsEnabled } from './analyticsConfig';
import { AppSettings } from '../directus/directusClient';
import { AnalyticsClient } from './AnalyticsClient';
import { AnalyticsEventName, AnalyticsEventParams } from './analyticsEvents';

// No-op analytics client for when settings are loading
class NoOpAnalyticsClient implements AnalyticsClient {
  initialise(): void {}
  trackEvent(_name: AnalyticsEventName, _params: AnalyticsEventParams): void {}
}

const AnalyticsServiceContext = createContext<AnalyticsService | null>(null);

interface AnalyticsServiceProviderProps {
  children: ReactNode;
  appSettings?: AppSettings | null;
}

export const AnalyticsServiceProvider: React.FC<AnalyticsServiceProviderProps> = ({
  children,
  appSettings
}) => {
  // Only create analytics service after app settings are loaded
  // This ensures we use the CMS measurement ID, not the default
  const analyticsService = useMemo(() => {
    if (appSettings === null) {
      // Settings not loaded yet - return a no-op service
      return null;
    }
    
    const measurementId = getMeasurementId(appSettings);
    const service = new AnalyticsService(
      new Ga4AnalyticsClient(measurementId),
      () => isAnalyticsEnabled(appSettings)
    );
    
    // Initialize immediately when created
    service.initialise();
    
    return service;
  }, [appSettings]);

  return (
    <AnalyticsServiceContext.Provider value={analyticsService}>
      {children}
    </AnalyticsServiceContext.Provider>
  );
};

// Singleton no-op service for when settings are loading
const noOpService = new AnalyticsService(new NoOpAnalyticsClient(), () => false);

export const useAnalyticsService = (): AnalyticsService => {
  const context = useContext(AnalyticsServiceContext);

  // Return no-op service while settings are loading
  // This prevents errors when components try to use analytics before settings are ready
  if (!context) {
    return noOpService;
  }

  return context;
};
